import { useState, useEffect, useCallback, Fragment } from 'react';
import { useNavigate, Navigate, useSearchParams, Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useMutation, DocumentNode, useLazyQuery } from '@apollo/client';
import { Backdrop, Checkbox, Divider, IconButton, Menu, MenuItem } from '@mui/material';

import { Button } from 'components/UI/Form/Button/Button';
import { Pager } from 'components/UI/Pager/Pager';
import { DialogBox } from 'components/UI/DialogBox/DialogBox';
import { SearchBar } from 'components/UI/SearchBar/SearchBar';
import { Tooltip } from 'components/UI/Tooltip/Tooltip';

import MoreOptions from 'assets/images/icons/MoreOptions.svg?react';
import DeleteIcon from 'assets/images/icons/Delete/Red.svg?react';
import EditIcon from 'assets/images/icons/Edit.svg?react';
import BackIcon from 'assets/images/icons/BackIconFlow.svg?react';
import AddIcon from 'assets/images/add.svg?react';
import { GET_CURRENT_USER } from 'graphql/queries/User';
import { getUserRole, getUserRolePermissions } from 'context/role';
import { setNotification, setErrorMessage } from 'common/notification';
import { getUpdatedList, setListSession, getLastListSessionValues } from 'services/ListService';
import styles from './List.module.css';
import Track from 'services/TrackService';
import HelpIcon from 'components/UI/HelpIcon/HelpIcon';
import { HelpDataProps } from 'common/HelpData';

const actionListMap = (item: any, actionList: any, hasMoreOption: boolean) => {
  return actionList.map((action: any, index: number) => {
    // check if we are dealing with nested element
    let additionalActionParameter: any;
    const params: any = action.parameter.split('.');
    if (params.length > 1) {
      additionalActionParameter = item[params[0]][params[1]];
    } else {
      additionalActionParameter = item[params[0]];
    }
    const key = index;

    if (action.hidden) {
      return null;
    }
    if (hasMoreOption) {
      return (
        <Fragment key={key}>
          <Divider className={styles.Divider} />
          <MenuItem className={styles.MenuItem}>
            <div
              data-testid="additionalButton"
              id="additionalButton-icon"
              onClick={() => action.dialog(additionalActionParameter, item)}
            >
              <div className={styles.IconWithText}>
                {action.icon}
                <div className={styles.TextButton}>{action.label}</div>
              </div>
            </div>
          </MenuItem>
        </Fragment>
      );
    }

    if (action.link) {
      return (
        <Link to={`${action.link}/${additionalActionParameter}`} key={key}>
          <Tooltip title={`${action.label}`} placement="top">
            <IconButton className={styles.additonalButton} data-testid="additionalButton">
              {action.icon}
            </IconButton>
          </Tooltip>
        </Link>
      );
    }
    if (action.dialog) {
      return (
        <Tooltip title={`${action.label}`} placement="top" key={key}>
          <IconButton
            data-testid="additionalButton"
            className={styles.additonalButton}
            id="additionalButton-icon"
            onClick={() => action.dialog(additionalActionParameter, item)}
            key={key}
          >
            {action.icon}
          </IconButton>{' '}
        </Tooltip>
      );
    }
    return null;
  });
};
export interface ColumnNames {
  name?: string;
  label: any;
  sort?: boolean;
  order?: string;
}

export interface ListProps {
  descriptionBox?: any;
  loadingList?: boolean;
  columnNames?: Array<ColumnNames>;
  countQuery?: DocumentNode;
  listItem: string;
  filterItemsQuery: DocumentNode;
  deleteItemQuery: DocumentNode | null;
  listItemName: string;
  dialogMessage?: string | any;
  pageLink: string;
  columns: Function;
  listIcon?: React.ReactNode;
  helpData?: HelpDataProps;
  columnStyles: Array<any>;
  secondaryButton?: any;
  title: string;
  button?: {
    show: boolean;
    label?: string;
    link?: string;
    action?: Function;
    symbol?: any;
  };
  searchParameter?: Array<any>;
  filters?: Object | null;
  filterList?: any;

  editSupport?: boolean;
  additionalAction?: (listValues: any) => Array<{
    icon?: any;
    parameter: string;
    link?: string;
    dialog?: any;
    label?: string;
    button?: any;
  }>;
  deleteModifier?: {
    variables: any;
  };
  dialogTitle?: string;
  backLink?: string;

  restrictedAction?: any;
  collapseOpen?: boolean;
  collapseRow?: string;
  // Opt-in transform applied to the fetched rows before they are rendered,
  // e.g. to group flat per-language rows into one row per template.
  groupRows?: (items: any[]) => any[];
  showActions?: boolean;
  defaultSortBy?: string | null;
  noItemText?: string | null;
  customStyles?: any;
  showHeader?: boolean;
  refreshList?: boolean;
  showSearch?: boolean;
  searchActive?: boolean;

  refetchQueries?: any;

  checkbox?: { show: boolean; action: any; selectedItems: any[]; setSelectedItems: any; icon: any };
  sortConfig?: {
    sortBy: string;
    sortOrder: 'asc' | 'desc';
  };
}

interface TableVals {
  pageNum: number;
  pageRows: number;
  sortCol: string;
  sortDirection: 'asc' | 'desc';
}

export const List = ({
  descriptionBox = <></>,
  loadingList = false,
  columnNames = [],
  countQuery,
  listItem,
  filterItemsQuery,
  deleteItemQuery,
  listItemName,
  dialogMessage = '',
  helpData,
  secondaryButton,
  pageLink,
  columns,
  columnStyles,
  title,
  dialogTitle,
  filterList,
  button = {
    show: true,
    label: 'Add New',
  },
  deleteModifier = { variables: null },
  editSupport = true,
  searchParameter = ['label'],
  filters = null,
  refreshList = false,
  additionalAction = () => [],
  backLink,

  restrictedAction,
  collapseOpen = false,
  collapseRow = undefined,
  groupRows,
  noItemText = null,
  customStyles,
  showActions = true,
  showHeader = true,
  showSearch = true,
  searchActive = false,

  refetchQueries,

  checkbox,
  sortConfig,
}: ListProps) => {
  const { t } = useTranslation();
  const [showMoreOptions, setShowMoreOptions] = useState<string>('');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };

  const navigate = useNavigate();

  // DialogBox states
  const [deleteItemID, setDeleteItemID] = useState<number | null>(null);
  const [deleteItemName, setDeleteItemName] = useState<string>('');
  const [newItem, setNewItem] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  // pick the search value from url if present
  const [searchVal, setSearchVal] = useState(new URLSearchParams(searchParams).get('search') || '');

  // check if the user has access to manage collections
  const userRolePermissions = getUserRolePermissions();
  const capitalListItemName = listItemName ? listItemName[0].toUpperCase() + listItemName.slice(1) : '';

  // function to get the default sorting set for columns
  const getDefaultSortColumn = (columnsFields: any) => {
    const sortColumn = columnsFields.find((field: any) => (field.sort ? field : ''));
    if (sortColumn) {
      return [sortColumn.name, sortColumn.order];
    }

    // if nothing is set assume first column is for sorting and order is 'asc'
    return [columnNames[0].name, 'asc'];
  };

  const [defaultColumnSort, defaultColumnSortOrder] = getDefaultSortColumn(columnNames);

  // get the last sort column value from local storage if exist else set the default column
  const getSortColumn = (listItemNameValue: string) => {
    // set the column name
    let columnnNameValue = defaultColumnSort;

    // check if we have sorting stored in local storage
    const sortValue = getLastListSessionValues(listItemNameValue, false);
    // update column name from the local storage
    if (sortValue) {
      columnnNameValue = sortValue;
    } else if (sortConfig && sortConfig.sortBy) {
      columnnNameValue = sortConfig.sortBy;
    }
    return columnnNameValue;
  };

  // get the last sort direction value from local storage if exist else set the default order
  const getSortDirection = (listItemNameValue: string) => {
    let sortDirection = defaultColumnSortOrder;

    // check if we have sorting stored in local storage
    const sortValue = getLastListSessionValues(listItemNameValue, true);
    if (sortValue) {
      sortDirection = sortValue;
    } else if (sortConfig && sortConfig.sortOrder) {
      sortDirection = sortConfig.sortOrder;
    }

    return sortDirection;
  };

  // Table attributes
  const [tableVals, setTableVals] = useState<TableVals>({
    pageNum: 0,
    pageRows: 50,
    sortCol: getSortColumn(listItemName),
    sortDirection: getSortDirection(listItemName),
  });

  let userRole: any = getUserRole();

  const handleTableChange = (attribute: string, newVal: any) => {
    const isSortAttribute = attribute === 'sortCol' || attribute === 'sortDirection';
    if (isSortAttribute) {
      const updatedList = getUpdatedList(listItemName, newVal, attribute === 'sortDirection');
      setListSession(JSON.stringify(updatedList));
    }

    setTableVals({
      ...tableVals,
      [attribute]: newVal,
    });
  };

  let filter: any = {};

  if (searchVal !== '') {
    searchParameter.forEach((parameter: string) => {
      filter[parameter] = searchVal;
    });
  }
  filter = {
    ...filter,
    ...filters,
  };

  const filterPayload = useCallback(() => {
    let order = 'ASC';
    if (tableVals.sortDirection) {
      order = tableVals.sortDirection.toUpperCase();
    }

    return {
      filter,
      opts: {
        limit: tableVals.pageRows,
        offset: tableVals.pageNum * tableVals.pageRows,
        order,
        orderWith: tableVals.sortCol,
      },
    };
  }, [searchVal, tableVals, filters]);

  // Get the total number of items here

  let refetchCount: any;
  let countData;
  let e;
  let l;

  if (countQuery) {
    [, { data: countData, error: e, loading: l, refetch: refetchCount }] = useLazyQuery(countQuery, {
      variables: { filter },
    });
  }

  // Get item data here
  const [, { loading, error, data, refetch: refetchValues }] = useLazyQuery(filterItemsQuery, {
    variables: filterPayload(),
    fetchPolicy: 'cache-first',
  });

  // Get item data here
  const [fetchUserCollections, { loading: loadingCollections, data: userCollections }] = useLazyQuery(GET_CURRENT_USER);

  const checkUserRole = () => {
    userRole = getUserRole();
  };

  useEffect(() => {
    // Todo: refetching values twice. Need to think of a better way to do this
    refetchValues();
    if (countQuery) {
      refetchCount();
    }
  }, [searchVal, filters, refreshList]);

  useEffect(() => {
    if (userRole.length === 0) {
      checkUserRole();
    } else {
      if (!userRolePermissions.manageCollections && listItem === 'collections') {
        // if user role staff then display collections related to login user
        fetchUserCollections();
      }
      Track(`Visit ${listItemName}`);
    }
  }, []);

  let deleteItem: any;

  // Make a new count request for a new count of the # of rows from this query in the back-end.
  if (deleteItemQuery) {
    [deleteItem] = useMutation(deleteItemQuery, {
      refetchQueries: () => {
        if (refetchQueries)
          return refetchQueries.map((refetchQuery: any) => ({
            query: refetchQuery.query,
            variables: refetchQuery.variables,
          }));
        return [];
      },
    });
  }

  const showDialogHandler = (id: any, label: string) => {
    setDeleteItemName(label);
    setDeleteItemID(id);
  };

  const closeDialogBox = () => {
    setDeleteItemID(null);
  };

  const deleteHandler = async (id: number) => {
    const variables = deleteModifier.variables ? deleteModifier.variables(id) : { id };
    try {
      await deleteItem({ variables });
      setNotification(`${capitalListItemName} deleted successfully`);
      checkUserRole();
      countQuery && refetchCount();
      if (refetchValues) {
        refetchValues(filterPayload());
      }
    } catch {
      setNotification(`Sorry! An error occurred!`, 'warning');
    }
  };

  const handleDeleteItem = () => {
    if (deleteItemID !== null) {
      deleteHandler(deleteItemID);
    }
    setDeleteItemID(null);
  };

  const useDelete = (message: string | any) => {
    if (typeof message === 'string') {
      const props = { handleOk: handleDeleteItem };
      return {
        component: message,
        props,
      };
    } else {
      /**
       * Custom component to render
       * message should contain 3 params
       * 1. component: Component to render
       * 2. props: props to pass to dialog component
       */

      const dialogParams = {
        deleteItemID,
        deleteItemName,
        refetch: refetchValues,
        setDeleteItemID,
      };
      const { component, props } = message(dialogParams);
      if (!props.handleOk) {
        props.handleOk = handleDeleteItem;
      }
      return {
        component,
        props,
      };
    }
  };

  let dialogBox;
  if (deleteItemID) {
    const { component, props } = useDelete(dialogMessage);
    dialogBox = (
      <DialogBox
        title={dialogTitle || `Are you sure you want to delete the ${listItemName} "${deleteItemName}"?`}
        handleCancel={closeDialogBox}
        colorOk="warning"
        alignButtons="center"
        {...props}
      >
        <div className={styles.DialogText}>
          <div>{component}</div>
        </div>
      </DialogBox>
    );
  }

  if (newItem) {
    return <Navigate to={`/${pageLink}/add`} />;
  }

  if (error || e) {
    if (error) {
      setErrorMessage(error);
    } else if (e) {
      setErrorMessage(e);
    }
    return null;
  }

  // Reformat all items to be entered in table
  function getIcons(
    // id: number | undefined,
    item: any,
    allowedAction: any | null
  ) {
    // there might be a case when we might want to allow certain actions for reserved items
    // currently we don't allow edit or delete for reserved items. hence return early
    const { id, label, name, isReserved } = item;

    let labelValue = label;

    if (name) {
      labelValue = name;
    }

    if (isReserved) {
      return null;
    }
    let moreButton = null;

    let editButton = null;
    if (editSupport) {
      editButton = allowedAction.edit && (
        <Link to={`/${pageLink}/${id}/edit`} className={styles.NoTextDecoration}>
          <Tooltip title="Edit" placement="top">
            <IconButton className={styles.additonalButton}>
              <div aria-label={t('Edit')} data-testid="EditIcon">
                <div className={styles.IconWithText}>
                  <EditIcon className={styles.IconSize} />
                </div>
              </div>
            </IconButton>
          </Tooltip>
        </Link>
      );
    }
    const deleteButtonMenuItem = (Id: any, text: string) =>
      allowedAction.delete ? (
        <div aria-label={t('Delete')} data-testid="DeleteIcon" onClick={() => showDialogHandler(Id, text)}>
          <div className={styles.IconWithText}>
            <DeleteIcon className={styles.IconSize} />
            <div className={styles.TextButton}>Delete</div>
          </div>
        </div>
      ) : null;

    const deleteButtonIcon = (Id: any, text: string) =>
      allowedAction.delete ? (
        <Tooltip title="Delete" placement="top">
          <IconButton
            className={styles.additonalButton}
            data-testid="DeleteIcon"
            onClick={() => showDialogHandler(Id, text)}
          >
            <DeleteIcon className={styles.IconSize} />
          </IconButton>
        </Tooltip>
      ) : null;

    const allAdditionalActions = additionalAction(item).filter((action: any) => !action?.hidden);

    let totalActions = allAdditionalActions.length;

    if (allowedAction.edit && editSupport) totalActions++;
    if (allowedAction.delete) totalActions++;

    const shouldShowMoreButton = totalActions > 4;

    let actionsOutsideMore = allAdditionalActions;
    let actionsInsideMore: any[] = [];
    if (shouldShowMoreButton) {
      let slotsAvailable = 4;
      if (allowedAction.edit && editSupport) slotsAvailable--;
      slotsAvailable--;

      actionsOutsideMore = allAdditionalActions.slice(0, slotsAvailable);
      actionsInsideMore = allAdditionalActions.slice(slotsAvailable);
    }

    if (shouldShowMoreButton) {
      moreButton = (
        <IconButton
          data-testid="MoreIcon"
          onClick={(event) => {
            setAnchorEl(event.currentTarget);
            setShowMoreOptions(showMoreOptions == id ? '' : id);
          }}
        >
          <Tooltip title={t('More')} placement="top">
            <div className={styles.MoreOptionsIcon}>
              <MoreOptions />
            </div>
          </Tooltip>
        </IconButton>
      );
    }

    if (id) {
      return (
        <div className={styles.Icons}>
          {actionListMap(item, actionsOutsideMore, false)}
          {allowedAction.edit && editButton}

          {/* do not display edit & delete for staff role in collection */}
          {userRolePermissions.manageCollections || item !== 'collections' ? (
            <>
              {!shouldShowMoreButton && deleteButtonIcon(id, labelValue)}

              <div className={styles.MoreOptions}>
                {moreButton}
                {showMoreOptions == id && (
                  <Backdrop className={styles.Backdrop} open onClick={() => setShowMoreOptions('')}>
                    <Menu
                      anchorEl={anchorEl}
                      id="account-menu"
                      open={open}
                      onClose={handleClose}
                      onClick={handleClose}
                      classes={{ list: styles.MenuList }}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                    >
                      <div>
                        {actionListMap(item, actionsInsideMore, true)}
                        <Divider className={styles.Divider}></Divider>
                        <MenuItem className={styles.MenuItem}>{deleteButtonMenuItem(id, labelValue)}</MenuItem>
                      </div>
                    </Menu>
                  </Backdrop>
                )}
              </div>
            </>
          ) : null}
        </div>
      );
    }
    return null;
  }

  const getCheckbox = (listItem: any) => {
    if (checkbox && checkbox.show)
      return {
        checkbox: (
          <div className={styles.CheckboxLabel}>
            <Checkbox
              data-testid="checkbox"
              checked={checkbox?.selectedItems.some((item: any) => item.id === listItem.id)}
              onChange={(event: any) => {
                if (event.target.checked) {
                  checkbox?.setSelectedItems([...checkbox?.selectedItems, listItem]);
                } else {
                  checkbox?.setSelectedItems(checkbox?.selectedItems.filter((item: any) => item.id !== listItem.id));
                }
              }}
            />
          </div>
        ),
      };
  };

  function formatList(listItems: Array<any>) {
    return listItems
      ? listItems.map(({ ...listItemObj }) => {
          // display only actions allowed to the user
          const allowedAction = restrictedAction
            ? restrictedAction(listItemObj)
            : { chat: true, edit: true, delete: true };

          const items = {
            ...getCheckbox(listItemObj),
            ...columns(listItemObj),

            recordId: listItemObj.id,
            isActive: listItemObj.isActive,
          };
          if (showActions) {
            items.operations = getIcons(listItemObj, allowedAction);
          }
          return items;
        })
      : [];
  }

  const resetTableVals = () => {
    setTableVals({
      pageNum: 0,
      pageRows: 50,
      sortCol: getSortColumn(listItemName),
      sortDirection: getSortDirection(listItemName),
    });
  };

  const handleSearch = (searchError: any) => {
    searchError.preventDefault();
    const searchValInput = searchError.target.querySelector('input').value.trim();

    setSearchParams({
      search: searchValInput,
    });
    setSearchVal(searchValInput);
    resetTableVals();
  };

  // Get item data and total number of items.
  let itemList: any = [];
  if (data) {
    const rawItems = data[listItem];
    itemList = formatList(groupRows ? groupRows(rawItems) : rawItems);
  }

  if (userCollections) {
    if (listItem === 'collections') {
      itemList = formatList(userCollections.currentUser.user.groups);
    }
  }

  let itemCount: number = tableVals.pageRows;
  if (countData) {
    itemCount = countData[`count${listItem[0].toUpperCase()}${listItem.slice(1)}`];
  }
  // Grouped lists (groupRows) collapse several flat records into fewer rows on
  // screen, so this count won't match the row count exactly. Still use the flat
  // server count (not the current page's post-grouping length) for pagination:
  // the offset/limit query params paginate the flat data, so total pages must be
  // derived from the flat total or later pages become unreachable.

  var noItemsText = (
    <div className={styles.NoResults}>
      {searchVal || searchActive ? (
        <div>{t('Sorry, no results found! Please try a different search.')}</div>
      ) : (
        <div>
          There are no {noItemText || listItemName}s right now. {button.show && t('Please create one.')}
        </div>
      )}
    </div>
  );

  if (checkbox && checkbox.show) {
    columnStyles = [styles.Checkbox, ...columnStyles];
    columnNames = [
      {
        label: (
          <Checkbox
            checked={checkbox?.selectedItems.length !== 0 && checkbox?.selectedItems.length === itemList.length}
            onChange={(event) => {
              if (event.target.checked) {
                checkbox?.setSelectedItems(data[listItem]);
              } else {
                checkbox?.setSelectedItems([]);
              }
            }}
          />
        ),
      },
      ...columnNames,
    ];
  }

  const handleCheckBoxAction = () => {
    checkbox?.action(checkbox?.selectedItems);
  };

  const displayList = (
    <Pager
      columnStyles={columnStyles}
      columnNames={columnNames}
      data={itemList}
      totalRows={itemCount}
      handleTableChange={handleTableChange}
      tableVals={tableVals}
      collapseOpen={collapseOpen}
      collapseRow={collapseRow}
      loadingList={loadingList || loading || l || loadingCollections}
      noItemsText={noItemsText}
      showPagination={countQuery ? true : false}
      checkboxSupport={{
        icon: checkbox?.icon,
        action: handleCheckBoxAction,
        selectedItems: checkbox?.selectedItems,
      }}
    />
  );

  let buttonDisplay;

  if (button.show) {
    const addIcon = <AddIcon className={styles.AddIcon} />;
    if (!button.symbol) {
      button.symbol = addIcon;
    }

    let buttonContent;
    if (button.action) {
      buttonContent = (
        <Button
          color="primary"
          variant="contained"
          onClick={() => button.action && button.action()}
          data-testid="newItemButton"
        >
          {button.symbol} {button.label}
        </Button>
      );
    } else if (!button.link) {
      buttonContent = (
        <Button color="primary" variant="contained" onClick={() => setNewItem(true)} data-testid="newItemButton">
          {button.symbol} {button.label}
        </Button>
      );
    } else {
      buttonContent = (
        <Link to={button.link}>
          <Button color="primary" variant="contained" data-testid="newItemLink">
            {button.symbol} {button.label}
          </Button>
        </Link>
      );
    }
    buttonDisplay = <div className={styles.AddButton}>{buttonContent}</div>;
  }

  const searchBar = showSearch ? (
    <div className={styles.Buttons}>
      <SearchBar
        handleSubmit={handleSearch}
        onReset={() => {
          setSearchParams({ search: '' });
          setSearchVal('');
          resetTableVals();
        }}
        searchVal={searchVal}
        handleChange={(err: any) => {
          // reset value only if empty
          if (!err.target.value) setSearchVal('');
        }}
        searchMode
      />
    </div>
  ) : null;

  return (
    <div className={`${showHeader ? styles.FullHeight : ''} ${styles.ListContainer}`}>
      {showHeader && (
        <>
          <div className={styles.Header} data-testid="listHeader">
            <div>
              <div className={styles.Title}>
                {backLink && (
                  <BackIcon onClick={() => navigate(backLink)} className={styles.BackLink} data-testid="back-button" />
                )}
                <div className={styles.TitleText}> {title}</div>
                {helpData && <HelpIcon helpData={helpData} />}
              </div>
            </div>
            <div>
              {dialogBox}
              <div className={styles.ButtonGroup}>
                {secondaryButton}
                {buttonDisplay}
              </div>
            </div>
          </div>
          {/* description box */}
          {descriptionBox}

          <div className={styles.FilterFields}>
            <div className={styles.FlexCenter}>{filterList}</div>
            {searchBar}
          </div>
        </>
      )}
      <div className={`${styles.Body} ${customStyles}`}>
        {/* Rendering list of items */}
        {displayList}
      </div>
    </div>
  );
};
